// Engine-neutral geometry helper: Traccar geofence WKT `area` -> lat/lng
// bounding box. Kept next to the controller (not in map/mapUtil, which produces
// Mapbox GeoJSON) so both adapters can consume one shared box shape; each
// adapter converts it to its own engine's bounds type in fitBoundingBox.
//
// Traccar stores coordinates as "lat lng" pairs (note: the opposite order from
// GeoJSON), e.g.
//   CIRCLE(36.8 10.18, 250)
//   POLYGON((36.8 10.1, 36.9 10.2, 36.7 10.3))
//   LINESTRING(36.8 10.1, 36.9 10.2)

const METERS_PER_DEGREE_LAT = 111320;

// Pull every "lat lng" pair out of the WKT body, ignoring the type keyword,
// parentheses and any trailing scalar (the circle radius is handled separately).
const parsePoints = (body) => body
    .split(',')
    .map((pair) => pair.replace(/[()]/g, ' ').trim().split(/\s+/).map(Number))
    .filter((nums) => nums.length >= 2 && nums.every((n) => Number.isFinite(n)))
    .map(([latitude, longitude]) => ({ latitude, longitude }));

const boundsOfPoints = (points) => {
    if (!points.length) return null;
    const latitudes = points.map((p) => p.latitude);
    const longitudes = points.map((p) => p.longitude);
    return {
        south: Math.min(...latitudes),
        north: Math.max(...latitudes),
        west: Math.min(...longitudes),
        east: Math.max(...longitudes),
    };
};

// A zero-area box (single-point LINESTRING, or a geofence whose corners
// coincide) makes fitBounds zoom to its maximum. Pad it into a small real box.
const MIN_SPAN_DEGREES = 0.0005;

const ensureSpan = (bounds) => {
    if (!bounds) return null;
    const latPad = Math.max(0, (MIN_SPAN_DEGREES - (bounds.north - bounds.south)) / 2);
    const lngPad = Math.max(0, (MIN_SPAN_DEGREES - (bounds.east - bounds.west)) / 2);
    return {
        south: bounds.south - latPad,
        north: bounds.north + latPad,
        west: bounds.west - lngPad,
        east: bounds.east + lngPad,
    };
};

// Returns { north, south, east, west } in degrees, or null when the area is
// missing or unparseable (callers then leave the camera alone).
export const geofenceToBounds = (geofence) => {
    const area = geofence && geofence.area && geofence.area.trim();
    if (!area) return null;

    if (area.startsWith('CIRCLE')) {
        const [latitude, longitude, radius] = area
            .replace(/CIRCLE|\(|\)|,/g, ' ')
            .trim()
            .split(/\s+/)
            .map(Number);
        if (![latitude, longitude, radius].every(Number.isFinite)) return null;
        const latDelta = radius / METERS_PER_DEGREE_LAT;
        // Longitude degrees shrink toward the poles; guard the cos->0 case.
        const cosLat = Math.max(Math.cos((latitude * Math.PI) / 180), 1e-6);
        const lngDelta = radius / (METERS_PER_DEGREE_LAT * cosLat);
        return ensureSpan({
            south: latitude - latDelta,
            north: latitude + latDelta,
            west: longitude - lngDelta,
            east: longitude + lngDelta,
        });
    }

    const body = area.slice(area.indexOf('('));
    return ensureSpan(boundsOfPoints(parsePoints(body)));
};
